/**
 * Obrava design tokens — single source of truth for color, typography,
 * spacing and radii. PURE module: no React, no DOM, no app imports.
 *
 * Hex / rgba literals live ONLY in this file. Apps and components must
 * consume the resolved {@link Theme} via semantic token names, never hex.
 *
 * Concrete values come from docs/plan/04-i18n-and-design.md.
 * Light and dark share an identical key set (enforced by the {@link Theme}
 * type below): switching theme is a change of values, never of structure.
 */

// ---------------------------------------------------------------------------
// Color — semantic palette (light + dark value sets, identical keys)
// ---------------------------------------------------------------------------

/**
 * Semantic color tokens. Each app screen references these names only; the
 * `light` / `dark` objects below provide the concrete values. The flat,
 * semantic naming (`surface`, `textPrimary`, `primary`, `danger`…) keeps
 * component code renderer-agnostic and theme-agnostic.
 */
export interface ColorTokens {
  // Brand
  readonly primary: string;
  readonly primaryHover: string;
  readonly primaryActive: string;
  readonly onPrimary: string;
  readonly primarySubtle: string;

  // Accent
  readonly accent: string;
  readonly accentSubtle: string;

  // Surfaces & background
  readonly canvas: string;
  readonly surface: string;
  readonly surfaceRaised: string;
  readonly surfaceSunken: string;
  readonly surfaceOverlay: string;
  readonly scrim: string;

  // Interaction states on warm surfaces
  /** Chosen element: active nav item, picked row or filter (ink = textPrimary / accent). */
  readonly selected: string;
  /** Soft corporate grey hover: menu items, card buttons. */
  readonly hover: string;

  // Borders & dividers
  readonly border: string;
  readonly borderStrong: string;
  /** A control border that must be visible on its own (3:1 on surface). */
  readonly borderControl: string;
  readonly borderFocus: string;
  readonly divider: string;

  // Elevation — the cast color used by RN shadowColor / native elevation.
  readonly shadow: string;

  // Text
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly textTertiary: string;
  readonly textDisabled: string;
  readonly textInverse: string;
  readonly textLink: string;

  // Status — each has base / background fill / accessible text-on-fill
  readonly success: string;
  readonly successBg: string;
  readonly successText: string;
  readonly warning: string;
  readonly warningBg: string;
  readonly warningText: string;
  readonly danger: string;
  readonly dangerBg: string;
  readonly dangerText: string;
  readonly info: string;
  readonly infoBg: string;
  readonly infoText: string;
  readonly neutralBg: string;
  readonly neutralText: string;

  // Article classification — identity, independent of operational severity.
  readonly materialBg: string;
  readonly materialText: string;
  readonly intermediateBg: string;
  readonly intermediateText: string;
  readonly finishedBg: string;
  readonly finishedText: string;
  readonly auxiliaryBg: string;
  readonly auxiliaryText: string;

  // AI — low-confidence reuses warning amber; AI surface is a calm violet
  readonly aiLowConfidence: string;
  readonly aiLowConfidenceBg: string;
  readonly aiSurface: string;
  readonly aiAccent: string;

  // Connection status (always-visible offline-first chip)
  readonly connectionOnline: string;
  readonly connectionOffline: string;
  readonly connectionSyncing: string;
  readonly connectionError: string;

  // Custom numeric weighing keypad (planta)
  readonly keypadBg: string;
  readonly keypadKey: string;
  readonly keypadKeyText: string;
  readonly keypadKeyActive: string;

  // Floating navigation (planta): translucent pill + the peach chosen item
  readonly glass: string;
  readonly navSelected: string;
  readonly onNavSelected: string;

  // Solid connection pill (planta header): identical in Light and Dark, white text
  readonly pillOnline: string;
  readonly pillOffline: string;
  readonly pillSyncing: string;
  readonly pillDanger: string;
  readonly onPill: string;

  // Focus ring
  readonly focusRing: string;
}

/*
 * Valors del sistema de disseny d'Obrava (artifact «Obrava», 2026-10-15).
 * Rol de marca: ocre. Estats: info (en curs) · pending (pendent / incidència) ·
 * done (completat) · warning (avís). Cada estat porta fons discret + tinta
 * (4.5:1) i sempre paraula: el color mai és l'únic indicador.
 *
 * Mapatge cap als noms semàntics que ja consumeixen les apps:
 *   primary = brand · primarySubtle = secondary · accent = brand-ink
 *   success/danger/info/warning = done/pending/info/warning (fons + ink)
 * Derivats (no són al sistema): primaryHover/Active, surfaceRaised/Sunken,
 * divider, borderStrong, textTertiary/Disabled, keypad*, scrim.
 */
const lightColors: ColorTokens = {
  primary: '#9E4E17',
  primaryHover: '#8A4313',
  primaryActive: '#763811',
  onPrimary: '#FFFFFF',
  primarySubtle: '#FDEADA',

  accent: '#8A4313',
  accentSubtle: '#FDEADA',

  canvas: '#F4F5F0',
  surface: '#FFFFFF',
  surfaceRaised: '#FFFFFF',
  surfaceSunken: '#ECEAE4',
  surfaceOverlay: '#FFFFFF',
  scrim: 'rgba(22,22,22,0.45)',

  selected: '#F4D8C4',
  hover: '#ECEAE4',

  border: '#E8E5DE',
  borderStrong: '#D3CFC6',
  borderControl: '#8C857C',
  borderFocus: '#9E4E17',
  divider: '#EEECE6',

  shadow: '#161616',

  textPrimary: '#161616',
  textSecondary: '#5C5A56',
  textTertiary: '#67645F',
  textDisabled: '#A9A69F',
  textInverse: '#FFFFFF',
  textLink: '#8A4313',

  success: '#1F6B3A',
  successBg: '#DCF0E0',
  successText: '#1F6B3A',
  warning: '#8A5200',
  warningBg: '#FFF0D9',
  warningText: '#8A5200',
  danger: '#9B2C22',
  dangerBg: '#F8DCD7',
  dangerText: '#9B2C22',
  info: '#0B5D63',
  infoBg: '#D4EFF0',
  infoText: '#0B5D63',
  neutralBg: '#ECEAE4',
  neutralText: '#5C5A56',

  materialBg: '#EAEDDC',
  materialText: '#525252',
  intermediateBg: '#D9F0F2',
  intermediateText: '#0B5D63',
  finishedBg: '#FDEADA',
  finishedText: '#8A4313',
  auxiliaryBg: '#ECE2F4',
  auxiliaryText: '#3B3B3B',

  aiLowConfidence: '#8A5200',
  aiLowConfidenceBg: '#FFF0D9',
  aiSurface: '#FDEADA',
  aiAccent: '#8A4313',

  connectionOnline: '#1F6B3A',
  connectionOffline: '#8A5200',
  connectionSyncing: '#0B5D63',
  connectionError: '#9B2C22',

  keypadBg: '#ECEAE4',
  keypadKey: '#FFFFFF',
  keypadKeyText: '#161616',
  keypadKeyActive: '#F4D8C4',

  glass: 'rgba(244,243,239,0.86)',
  navSelected: '#EBC7A4',
  onNavSelected: '#161616',

  pillOnline: '#2E4B33',
  pillOffline: '#7A5226',
  pillSyncing: '#0B5D63',
  pillDanger: '#9B2C22',
  onPill: '#FFFFFF',

  focusRing: '#9E4E17',
};

const darkColors: ColorTokens = {
  primary: '#9E4E17',
  primaryHover: '#A95318',
  primaryActive: '#8A4313',
  onPrimary: '#FFF7EF',
  primarySubtle: '#2B241B',

  accent: '#F2A56B',
  accentSubtle: '#2B241B',

  canvas: '#0A0A0A',
  surface: '#161616',
  surfaceRaised: '#1D1D1D',
  surfaceSunken: '#0F0F0F',
  surfaceOverlay: '#1D1D1D',
  scrim: 'rgba(0,0,0,0.60)',

  selected: '#3B2410',
  hover: '#242424',

  border: '#2B2B2B',
  borderStrong: '#3F3F3F',
  borderControl: '#7A746C',
  borderFocus: '#E08A4A',
  divider: '#222222',

  shadow: '#000000',

  textPrimary: '#F5F2EE',
  textSecondary: '#A8A39C',
  textTertiary: '#8A857E',
  textDisabled: '#5F5B56',
  textInverse: '#161616',
  textLink: '#F2A56B',

  success: '#86D9A6',
  successBg: '#15301F',
  successText: '#86D9A6',
  warning: '#F4B860',
  warningBg: '#352417',
  warningText: '#F4B860',
  danger: '#FF9D92',
  dangerBg: '#331719',
  dangerText: '#FF9D92',
  info: '#6FD3DA',
  infoBg: '#0C2A2E',
  infoText: '#6FD3DA',
  neutralBg: '#242424',
  neutralText: '#A8A39C',

  materialBg: '#25291B',
  materialText: '#D8DDBF',
  intermediateBg: '#0C2A2E',
  intermediateText: '#6FD3DA',
  finishedBg: '#2B241B',
  finishedText: '#F2A56B',
  auxiliaryBg: '#2D2335',
  auxiliaryText: '#DCC5EC',

  aiLowConfidence: '#F4B860',
  aiLowConfidenceBg: '#352417',
  aiSurface: '#2B241B',
  aiAccent: '#F2A56B',

  connectionOnline: '#86D9A6',
  connectionOffline: '#F4B860',
  connectionSyncing: '#6FD3DA',
  connectionError: '#FF9D92',

  keypadBg: '#0F0F0F',
  keypadKey: '#1D1D1D',
  keypadKeyText: '#F5F2EE',
  keypadKeyActive: '#3B2410',

  glass: 'rgba(30,30,30,0.86)',
  navSelected: '#EBC7A4',
  onNavSelected: '#161616',

  pillOnline: '#2E4B33',
  pillOffline: '#7A5226',
  pillSyncing: '#0B5D63',
  pillDanger: '#9B2C22',
  onPill: '#FFFFFF',

  focusRing: '#E08A4A',
};

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

/**
 * Font family stacks. Space Grotesk for interface and communication, Space
 * Mono for technical data (order / lot codes, labels, status). Both are
 * loaded by each app (Google Fonts on web, expo-font on planta); the system
 * stack is the fallback while they load.
 */
export const fontFamily = {
  sans: '"Space Grotesk", -apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
  display: '"Space Grotesk", -apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
  numeric: '"Space Grotesk", -apple-system, system-ui, "Segoe UI", Roboto, sans-serif',
  mono: '"Space Mono", ui-monospace, "SF Mono", Menlo, monospace',
} as const;

/** Numeric font weights (kept as numbers so RN can consume them directly). */
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** Font sizes in px (number). Web emits `px`; RN consumes the number. */
export const fontSize = {
  /** mono-data — codes, lots, status badges. */
  caption: 12,
  /** app-label — field labels, controls, floating nav. */
  footnote: 13,
  /** app-secondary — secondary text, card footers, field help. */
  secondary: 14,
  /** Base body text (dense backoffice screens); app-body is 16 on touch screens (bodyLg). */
  body: 15,
  bodyLg: 17,
  title3: 20,
  title2: 24,
  title1: 28,
  largeTitle: 34,
  display: 44,
  /** Big figure of an operative card (KPI). */
  metric: 32,
  /** Pesatge / consum — the big weighing readout. */
  weightReadout: 56,
} as const;

/** Unitless line-height multipliers (valid for both web and RN). */
export const lineHeight = {
  tight: 1.15,
  snug: 1.3,
  normal: 1.5,
  relaxed: 1.65,
} as const;

/** Letter spacing in em (web). RN can map via fontSize when needed. */
export const letterSpacing = {
  tight: '-0.02em',
  normal: '0em',
  wide: '0.02em',
} as const;

/**
 * Composite text recipes for the load-bearing UI moments. Keeping them as
 * structured objects (not CSS shorthand strings) lets both web and RN
 * consume the same recipe.
 */
export const textStyle = {
  /** Pesatge / consum —56px / 1.1 / 600 / tabular-nums. */
  weightReadout: {
    fontFamily: fontFamily.numeric,
    fontSize: fontSize.weightReadout,
    lineHeight: 1.1,
    fontWeight: fontWeight.semibold,
    tabularNums: true,
  },
  /** Teclat numèric propi — 28px / 1 / 500 / tabular-nums. */
  keypadKey: {
    fontFamily: fontFamily.numeric,
    fontSize: fontSize.title1,
    lineHeight: 1,
    fontWeight: fontWeight.medium,
    tabularNums: true,
  },
  /** Títol d'OT a targeta — 17px / 1.3 / 600. */
  cardTitle: {
    fontFamily: fontFamily.sans,
    fontSize: fontSize.bodyLg,
    lineHeight: lineHeight.snug,
    fontWeight: fontWeight.semibold,
    tabularNums: false,
  },
  /** Títol de pantalla (app-title) — 28px / 1.15 / 600 / -0.02em. */
  screenTitle: {
    fontFamily: fontFamily.display,
    fontSize: fontSize.title1,
    lineHeight: lineHeight.tight,
    fontWeight: fontWeight.semibold,
    tabularNums: false,
  },
} as const;

export interface TypographyTokens {
  readonly family: Readonly<Record<keyof typeof fontFamily, string>>;
  readonly weight: typeof fontWeight;
  readonly size: typeof fontSize;
  readonly lineHeight: typeof lineHeight;
  readonly letterSpacing: typeof letterSpacing;
  readonly style: typeof textStyle;
}

const typography: TypographyTokens = {
  family: fontFamily,
  weight: fontWeight,
  size: fontSize,
  lineHeight,
  letterSpacing,
  style: textStyle,
};

// ---------------------------------------------------------------------------
// Spacing & sizing
// ---------------------------------------------------------------------------

/** Spacing scale in px (number). 2px base grid. */
export const space = {
  0: 0,
  1: 2,
  2: 4,
  3: 8,
  4: 12,
  5: 16,
  6: 20,
  7: 24,
  8: 32,
  9: 40,
  10: 48,
  11: 64,
  12: 80,
} as const;

/**
 * Fixed-size tokens in px (number). Planta variants are deliberately larger
 * for gloved use / high-contrast weighing; backoffice uses the macOS chrome.
 */
export const size = {
  touchTargetMin: 44,
  touchTargetPlanta: 56,
  touchTargetPlantaCTA: 64,
  keypadKey: 72,
  /** Web button / field row: compact 36px, like the dashboard reference. */
  ctaHeight: 36,
  ctaHeightPlanta: 64,
  iconSm: 16,
  iconMd: 20,
  iconLg: 24,
  statusDot: 8,
  /**
   * Height of a non-interactive chip/pill (e.g. the connection-status
   * indicator). Deliberately NOT derived from touchTargetMin: a chip is a
   * status badge, not a tap target, so it must not imply a 44px (or any)
   * interactive size.
   */
  chipHeight: 28,
  backofficeSidebar: 240,
  /**
   * Readable max width for a single-column FORM (article/recipe/work-order/
   * tenant editors). Forms stay narrow on wide displays so field rows keep a
   * comfortable line length — do NOT use this to cap whole screens.
   */
  contentMaxWidth: 1120,
  /**
   * Max width of the app-shell content area (the region to the right of the nav
   * that hosts each screen). Wider than a form so data-dense screens — catalog,
   * stock, dashboard, agenda, the tenant list — use the horizontal space; the
   * shell padding acts as the side margin. Only caps on very wide monitors so
   * line lengths stay sane; on laptops it effectively fills the available width.
   */
  shellMaxWidth: 1600,
  /** Max width of the centered auth/sign-in card on the full-viewport canvas. */
  authCardMaxWidth: 420,
  /** Max width of a modal dialog (e.g. the reason-capture dialog). */
  dialogMaxWidth: 480,
  /**
   * Max width of a modal that carries DATA TABLES rather than a short form or
   * confirmation (e.g. the recipe-version popup, which shows the BOM and the
   * elaboration steps). Wide enough for those columns to read like they do on
   * the screen behind it; still narrower than the shell so it reads as a layer.
   */
  dialogWideMaxWidth: 880,
  fieldHeight: 36,
  fieldHeightPlanta: 56,
  /** Painted height of a planta filter chip (hitSlop widens the touch area to 56). */
  filterChipPlanta: 48,
  screenPaddingPlanta: 20,
  screenPaddingBackoffice: 32,
  /**
   * Width of a narrow numeric input that sits in a flexible grid row beside
   * wider controls (e.g. the conversion-factor cell in the article card's
   * alternative-units editor). Sizing is scheme-independent, so light/dark
   * share this value by construction.
   */
  numericInputNarrow: 120,
  /**
   * Max width of a single short numeric field rendered on its own (e.g. the
   * recipe scaling preview's target-yield / factor input). Scheme-independent,
   * so light/dark share this value by construction.
   */
  numericInputMaxWidth: 280,
} as const;

export interface SpacingTokens {
  readonly space: typeof space;
  readonly size: typeof size;
}

const spacing: SpacingTokens = { space, size };

// ---------------------------------------------------------------------------
// Radii & shadows
// ---------------------------------------------------------------------------

/** Corner radii in px (number). */
export const radius = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  card: 12,
  button: 8,
  field: 8,
  chip: 9999,
  pill: 9999,
  keypadKey: 16,
} as const;

/**
 * Box shadows as web CSS strings. `focusRing` references the `--color-focusRing`
 * custom property emitted by the theme module, so it follows the active theme.
 * RN components should map elevation separately rather than parse these.
 */
export const shadow = {
  /** Mòbil: elements que suren (menú flotant, fulls). Dues capes. */
  mobile: '0 18px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.14)',
  /** Backoffice: cards operatives. */
  backoffice: '0 8px 24px rgba(0,0,0,0.10)',
  // Aliases. El sistema només admet les dues ombres de dalt: cap altra.
  sm: 'none',
  /** Cards are flat: a 1px border on the warm canvas, no shadow (dashboard reference). */
  card: 'none',
  /** Only on hover of an interactive card. */
  raised: '0 8px 24px rgba(0,0,0,0.10)',
  overlay: '0 18px 40px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.14)',
  /** Anell de focus sòlid de 2px; mai suprimit. */
  focusRing: '0 0 0 2px var(--color-focusRing)',
} as const;

export interface RadiiTokens {
  readonly radius: typeof radius;
  readonly shadow: typeof shadow;
}

const radii: RadiiTokens = { radius, shadow };

// ---------------------------------------------------------------------------
// Theme assembly
// ---------------------------------------------------------------------------

/** Identifier of the active color scheme. */
export type ThemeName = 'light' | 'dark';

/**
 * A fully resolved theme: a single color scheme plus the scheme-independent
 * typography / spacing / radii token groups. Light and dark produce objects
 * of this exact shape — identical key sets, only the color values differ.
 */
export interface Theme {
  readonly name: ThemeName;
  readonly color: ColorTokens;
  readonly typography: TypographyTokens;
  readonly spacing: SpacingTokens;
  readonly radii: RadiiTokens;
}

/** Resolved color sets keyed by scheme. */
export const colors: Readonly<Record<ThemeName, ColorTokens>> = {
  light: lightColors,
  dark: darkColors,
};

/**
 * The Backoffice's own DARK appearance (Figma «OBRAVA · Backoffice · Aparença», 11/10/2026, OBR-132).
 *
 * It is the dark set above with the values of the approved Backoffice design swapped in, and it is
 * used by the Backoffice ONLY: the planta, the console and the web keep `darkColors`, so converting
 * the Backoffice never moves a pixel of the other apps. Same keys, only values: a token this object
 * does not name keeps its dark value. The light appearance is untouched.
 *
 * Surfaces: page `#0A0A0A` (canvas), work area `#101010` (surfaceSunken), cards `#171717` (surface),
 * secondary surfaces `#222222` (surfaceRaised). Brand: CTA `#9E4E17`, links and selected ink `#EA7835`,
 * selected fill `#3D2514`, muted secondary button `#251E19`. Statuses: tinted dark fill + light ink.
 * Deliberately NOT swapped: `borderControl` (the design draws input borders close to `border`, which
 * is under the 3:1 a control needs to be seen) and `focusRing`.
 */
const backofficeDarkColors: ColorTokens = {
  ...darkColors,
  onPrimary: '#FFFFFF',
  primarySubtle: '#251E19',

  accent: '#EA7835',
  accentSubtle: '#251E19',

  surface: '#171717',
  surfaceRaised: '#222222',
  surfaceSunken: '#101010',

  selected: '#3D2514',

  border: '#343434',

  textPrimary: '#F5F5F5',
  textSecondary: '#B3B3B3',
  textTertiary: '#949494',
  textLink: '#EA7835',

  success: '#8EC79E',
  successBg: '#142B1D',
  successText: '#8EC79E',
  warning: '#E8BD83',
  warningBg: '#302317',
  warningText: '#E8BD83',
  danger: '#F0A29A',
  dangerBg: '#331A19',
  dangerText: '#F0A29A',
  info: '#7AD7DF',
  infoBg: '#102C30',
  infoText: '#7AD7DF',
};

/** The Backoffice's color sets: its own dark, and the shared light. */
export const backofficeColors: Readonly<Record<ThemeName, ColorTokens>> = {
  light: lightColors,
  dark: backofficeDarkColors,
};

/**
 * Scheme-independent token groups. Exposed for tools (e.g. CSS emission)
 * that need typography/spacing/radii without picking a color scheme.
 */
export const tokens = {
  typography,
  spacing,
  radii,
} as const;

export { typography, spacing, radii };

/** Marketing website palette from Figma DISSENY WEB; product tokens stay independent. */
export const webColors = {
  light: { canvas: '#F6F5F2', surface: '#FFFFFF', section: '#FFFFFF', textPrimary: '#0A0A0A', textSecondary: '#3B3B3B', textTertiary: '#3B3B3B', border: '#DCD8CB', accent: '#9E4E17', hover: '#EDEAE5', successBg: '#DCEDE1', successText: '#315C3C', infoBg: '#D9F0F2', infoText: '#197C85', warningBg: '#FDEADA', warningText: '#0A0A0A', faqSurface: '#FFFFFF', faqHover: '#EDEAE5', faqBorder: '#DCD8CB', faqText: '#0A0A0A', faqIndicator: '#9E4E17', faqOpenText: '#FFFFFF' },
  dark: { canvas: '#111111', section: '#0A0A0A', faqSurface: '#171717', faqHover: '#262626', faqBorder: '#323232', faqText: '#F5F4F0', faqIndicator: '#D9945F', faqOpenText: '#FFFFFF' },
} as const;

/** Figma APP mobile navigation; independent of other product surfaces. */
export const floatingNavColors = {
  // `badge` / `badgeInk`: the pending-tasks counter on a tab (OBR-134). A saturated red in both schemes (it
  // asks for action) with white ink at 4.5:1 or more. Not the pale dark-mode `danger` tone: the selected tab
  // is the same peach pill in Light and Dark, and a pale red on peach barely reads.
  light: { glass: 'rgba(255,255,255,0.42)', border: 'rgba(255,255,255,0.75)', ink: '#0A0A0A', fade: ['rgba(246,245,242,0)', 'rgba(246,245,242,0.22)', 'rgba(246,245,242,0.8)'], badge: '#9B2C22', badgeInk: '#FFFFFF' },
  dark: { glass: 'rgba(48,48,48,0.62)', border: 'rgba(255,255,255,0.2)', ink: '#FFFFFF', fade: ['rgba(10,10,10,0)', 'rgba(10,10,10,0.22)', 'rgba(10,10,10,0.8)'], badge: '#CC3F33', badgeInk: '#FFFFFF' },
  selected: '#F2C9A2', selectedInk: '#6F2F0B', shadow: '#000000',
} as const;
